import {
  CallResolution,
  StayInboxTouchVia,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { clipText } from '@new/domains/operations/utils/text'

import { GuestInboxStayDetail, GuestInboxTimelineEvent } from '../../StayTimeline.types'
import { getLastOutboundEvent, toDate, waitLabel } from '../../StayTimeline.utils'

export interface HandledStrip {
  isWarning: boolean
  head: string
  said?: string
  solved?: string
  work?: string
  ack: string
}

const CALL_SUMMARY_CLIP = 240
const MESSAGE_CLIP = 200

const getVerb = (via?: StayInboxTouchVia | null) => {
  if (via === StayInboxTouchVia.call) return 'spoke to the guest'
  if (via === StayInboxTouchVia.ticket) return 'answered on the ticket'

  return 'replied'
}

const getWork = (detail: GuestInboxStayDetail) => {
  const topCase = detail.cases.top

  if (topCase) {
    const owner = (topCase.owner ?? '').trim()
    const status = (topCase.status ?? '').trim()
    const state = status && status !== 'Unknown' ? status.toLowerCase() : 'still open'

    return `${topCase.categoryLabel} is ${state}${owner ? ` with ${owner.split(' ')[0]}` : ', unassigned'}`
  }

  const closed = detail.caseList.filter(item => !item.isOpen)
  if (!closed.length) return undefined

  const labels = [...new Set(closed.map(item => item.categoryLabel))].slice(0, 2)

  return `${closed.length} case${closed.length > 1 ? 's' : ''} closed this stay — ${labels.join(', ')}`
}

const getSolved = (isCall: boolean, resolution?: CallResolution | null) => {
  if (!isCall || !resolution) return undefined

  return resolution === CallResolution.resolved
    ? 'resolved on the call'
    : 'not resolved on the call'
}

const getAck = (detail: GuestInboxStayDetail, lastAt?: Date) => {
  const lastIn = toDate(detail.spokeThread?.lastInAt)

  if (lastIn && lastAt && lastIn > lastAt) {
    return detail.spokeThread?.awaitingReply
      ? 'the guest has written since and is still waiting'
      : 'the guest answered and let it rest'
  }

  return 'no word from the guest since'
}

export const getHandledStrip = (
  detail: GuestInboxStayDetail,
  conversation: GuestInboxTimelineEvent[],
): HandledStrip | undefined => {
  const { followUp } = detail.judgement
  if (!followUp.lastAt) return undefined

  const isCall = followUp.lastVia === StayInboxTouchVia.call
  const who = (followUp.lastWho ?? '').trim() || 'The team'
  const head = `${who} ${getVerb(followUp.lastVia)} · ${waitLabel(followUp.sinceHours)} ago`

  const lastCall = detail.calls[detail.calls.length - 1]
  const lastOutbound = getLastOutboundEvent(conversation)

  const said = isCall
    ? clipText(lastCall?.summary, CALL_SUMMARY_CLIP) || lastCall?.topic || undefined
    : clipText(lastOutbound?.body, MESSAGE_CLIP) || undefined

  return {
    isWarning: followUp.needs,
    head,
    said,
    solved: getSolved(isCall, lastCall?.resolution),
    work: getWork(detail),
    ack: getAck(detail, toDate(followUp.lastAt)),
  }
}
